import { Theme, useTheme } from "@amx/ux-twoui-theming";
import {
    ILabelStyles,
    Label as FluentLabel,
    Spinner,
    SpinnerSize
} from "@fluentui/react";
import React from "react";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, validateProps } from "../../shared/props";
import { Icon } from "../Icon/Icon";
import { Stack } from "../Stacks/Stack/Stack";

export type LabelState = "Error" | "Saving" | "Saved" | "Light";

const getLabelColor = (theme: Theme, labelState: LabelState): string => {
    switch (labelState) {
        case "Error": {
            return theme.palette.redDark;
        }
        case "Saving":
        case "Saved": {
            return theme.palette.themePrimary;
        }
        case "Light": {
            return theme.palette.neutralSecondary;
        }
    }
};

const getStyles = (theme: Theme, labelState?: LabelState): ILabelStyles => {
    switch (labelState) {
        case "Error":
        case "Saving":
        case "Saved": {
            return {
                root: {
                    height: 16,
                    fontWeight: "normal",
                    fontSize: 12,
                    lineHeight: 16,
                    padding: 0,
                    color: getLabelColor(theme, labelState)
                }
            };
        }
        case "Light": {
            return {
                root: {
                    fontWeight: "normal",
                    color: getLabelColor(theme, labelState)
                }
            };
        }
        default: {
            return {
                root: {}
            };
        }
    }
};

const getLabelIcon = (theme: Theme, labelState: LabelState) => {
    switch (labelState) {
        case "Saving": {
            return <Spinner ariaLive="assertive" size={SpinnerSize.xSmall} />;
        }
        case "Saved": {
            return (
                <Icon
                    iconName="CheckMark"
                    size="16px"
                    {...{
                        styles: { root: { color: theme.palette.blue } }
                    }}
                />
            );
        }
        default: {
            return;
        }
    }
};

export interface LabelProps extends BaseProps {
    /**
     * Determines additional states in which the label can render
     */
    readonly labelState?: LabelState;

    /**
     * Determines if the label will render as disabled
     */
    readonly disabled?: boolean;

    /**
     * Determines if the label will render with a required indicator
     */
    readonly required?: boolean;

    /**
     * Optional ID to be passed to Label for when it's labelling another
     * element. Passing through the ID of the other element helps gives
     * screenreaders context.
     */
    readonly htmlFor?: string;

    readonly children: React.ReactNode;
}

export const Label: React.FC<LabelProps> = ({
    labelState,
    disabled,
    required,
    htmlFor,
    children,
    ...otherProps
}) => {
    useComponentLogger("Label", otherProps.id);

    validateProps("Label", otherProps);

    const theme = useTheme();

    const styles = getStyles(theme, labelState);
    const labelElement = (
        <FluentLabel
            disabled={disabled}
            required={required}
            htmlFor={htmlFor}
            styles={styles}
            {...otherProps}
        >
            {children}
        </FluentLabel>
    );

    if (labelState !== undefined && labelState !== "Error") {
        return (
            <Stack horizontal={true} childrenGap={7}>
                {getLabelIcon(theme, labelState)}
                {labelElement}
            </Stack>
        );
    } else {
        return labelElement;
    }
};
